import { Link, useNavigate, useHydrated, useRouterState } from "@tanstack/react-router";
import { useState, useEffect, type ReactNode } from "react";
import { Menu, X, ArrowRight, LogOut, ChevronDown, Globe } from "lucide-react";
import { XSocialIcon } from "@/components/site/XSocialIcon";
import {
  FacebookIcon,
  InstagramIcon,
  YoutubeIcon,
  LinkedinIcon,
} from "@/components/site/SocialBrandIcons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SITE_SOCIAL } from "@/lib/site-info";
import { localizedPath } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { m } from "@/paraglide/messages.js";
import { getLocale, setLocale, type Locale } from "@/paraglide/runtime";
import { APP_LOCALES, LOCALE_SHORT_LABEL, type AppLocale } from "@/lib/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type NavLink = { to: string; label: string };
type NavGroup = { label: string; children: readonly NavLink[] };
type NavItem = NavLink | NavGroup;

function isNavGroup(item: NavItem): item is NavGroup {
  return "children" in item;
}

function useNavItems(): readonly NavItem[] {
  return [
    { to: "/", label: m.nav_home() },
    { to: "/method", label: m.nav_method() },
    { to: "/play-injury-free", label: m.nav_play_injury_free() },
    {
      label: m.nav_lessons_group(),
      children: [
        { to: "/lessons", label: m.nav_lesson_formats() },
        { to: "/prices", label: m.nav_prices() },
        { to: "/practice", label: m.nav_practice_hub() },
      ],
    },
    {
      label: m.nav_coach_group(),
      children: [
        { to: "/about", label: m.nav_about_mark() },
        { to: "/hall-of-fame", label: m.nav_hall_of_fame() },
      ],
    },
    { to: "/range", label: m.nav_range() },
    { to: "/blog", label: m.nav_blog() },
    { to: "/events", label: m.nav_events() },
  ];
}

const DESKTOP_NAV_LINK =
  "relative px-2.5 py-2 font-display text-sm tracking-[0.15em] text-foreground/75 transition-colors hover:text-ember";
const DESKTOP_NAV_LINK_ACTIVE =
  "relative px-2.5 py-2 font-display text-sm tracking-[0.15em] text-ember after:absolute after:inset-x-2.5 after:bottom-0 after:h-0.5 after:rounded-full after:bg-ember-fill";

const MOBILE_NAV_LINK =
  "border-s-2 border-transparent py-3 ps-3 font-display tracking-[0.15em] text-foreground/90 transition-colors hover:text-ember";
const MOBILE_NAV_LINK_ACTIVE =
  "border-s-2 border-ember py-3 ps-3 font-display tracking-[0.15em] text-ember";
const MOBILE_NAV_SUBLINK =
  "border-s-2 border-transparent py-2 ps-6 font-display text-sm tracking-[0.12em] text-foreground/75 transition-colors hover:text-ember";
const MOBILE_NAV_SUBLINK_ACTIVE =
  "border-s-2 border-ember py-2 ps-6 font-display text-sm tracking-[0.12em] text-ember";

const TOP_NAV_LINK = "shrink-0 transition-colors hover:text-ember";
const TOP_NAV_LINK_ACTIVE = "shrink-0 text-ember underline underline-offset-4 decoration-ember/80";

function pathMatches(pathname: string, to: string) {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(`${to}/`);
}

function groupIsActive(pathname: string, children: readonly NavLink[]) {
  return children.some((child) => pathMatches(pathname, child.to));
}

/** Plain <a> until hydrated so Link data-status/active classes cannot mismatch SSR. */
function HeaderLink({
  to,
  className,
  activeClassName,
  exact,
  children,
  onClick,
}: {
  to: string;
  className: string;
  activeClassName?: string;
  exact?: boolean;
  children: ReactNode;
  onClick?: () => void;
}) {
  const hydrated = useHydrated();
  if (!hydrated) {
    // Localize before the router rewrite runs so crawlers keep locale prefixes.
    const href = localizedPath(to, getLocale());
    return (
      <a href={href} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      activeProps={activeClassName ? { className: activeClassName } : undefined}
      activeOptions={exact !== undefined ? { exact } : undefined}
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function DesktopNavGroup({ item, pathname }: { item: NavGroup; pathname: string }) {
  const [open, setOpen] = useState(false);
  const [suppressHover, setSuppressHover] = useState(false);
  const active = groupIsActive(pathname, item.children);
  const show = open && !suppressHover;

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        if (!suppressHover) setOpen(true);
      }}
      onMouseLeave={() => {
        setOpen(false);
        setSuppressHover(false);
      }}
    >
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1",
          active ? DESKTOP_NAV_LINK_ACTIVE : DESKTOP_NAV_LINK,
        )}
        aria-haspopup="menu"
        aria-expanded={show}
        onFocus={() => {
          if (!suppressHover) setOpen(true);
        }}
        onClick={() => {
          setSuppressHover(false);
          setOpen((value) => !value);
        }}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </button>
      <div
        className={cn(
          "absolute start-0 top-full z-50 min-w-[12rem] border border-white/10 bg-ink py-1 shadow-lg transition-opacity",
          show ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
        )}
        role="menu"
      >
        {item.children.map((child) => (
          <HeaderLink
            key={child.to}
            to={child.to}
            onClick={() => {
              setOpen(false);
              setSuppressHover(true);
            }}
            className={cn(
              "block px-3 py-2 font-display text-sm tracking-[0.12em] text-foreground/80 transition-colors hover:bg-white/10 hover:text-ember",
              pathMatches(pathname, child.to) && "text-ember",
            )}
          >
            {child.label}
          </HeaderLink>
        ))}
      </div>
    </div>
  );
}

function localeNativeName(locale: AppLocale): string {
  switch (locale) {
    case "en":
      return m.nav_language_en();
    case "es":
      return m.nav_language_es();
    case "de":
      return m.nav_language_de();
    case "zh":
      return m.nav_language_zh();
    case "ar":
      return m.nav_language_ar();
    case "fr":
      return m.nav_language_fr();
    case "vi":
      return m.nav_language_vi();
    case "sw":
      return m.nav_language_sw();
    case "th":
      return m.nav_language_th();
    case "bs":
      return m.nav_language_bs();
    case "ja":
      return m.nav_language_ja();
    case "ko":
      return m.nav_language_ko();
    case "pt":
      return m.nav_language_pt();
    case "it":
      return m.nav_language_it();
    case "sv":
      return m.nav_language_sv();
    case "nl":
      return m.nav_language_nl();
    case "da":
      return m.nav_language_da();
    case "nb":
      return m.nav_language_nb();
    case "fi":
      return m.nav_language_fi();
    case "pl":
      return m.nav_language_pl();
    case "ru":
      return m.nav_language_ru();
    case "tr":
      return m.nav_language_tr();
    case "hi":
      return m.nav_language_hi();
    case "id":
      return m.nav_language_id();
    case "ms":
      return m.nav_language_ms();
    case "cs":
      return m.nav_language_cs();
    case "el":
      return m.nav_language_el();
    case "he":
      return m.nav_language_he();
    case "af":
      return m.nav_language_af();
    case "hu":
      return m.nav_language_hu();
  }
}

function LanguageSwitcher({
  authed,
  profileLocale,
  className,
}: {
  authed: boolean;
  profileLocale: AppLocale | null | undefined;
  className?: string;
}) {
  const queryClient = useQueryClient();
  const current = getLocale();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!authed || !profileLocale) return;
    if (profileLocale === getLocale()) return;
    // Soft update avoids a full reload that can race authenticated beforeLoad session reads.
    void setLocale(profileLocale, { reload: false });
  }, [authed, profileLocale]);

  const choose = async (locale: Locale) => {
    if (locale === current) return;
    setOpen(false);
    if (authed) {
      try {
        const { updateMyPreferredLocale } = await import("@/lib/profile.functions");
        await updateMyPreferredLocale({ data: { locale } });
        await queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      } catch (err) {
        console.error(err);
      }
    }
    await setLocale(locale);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 transition-colors hover:text-ember",
            className,
          )}
          aria-label={`${LOCALE_SHORT_LABEL[current]}, ${m.nav_language()}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Globe className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{LOCALE_SHORT_LABEL[current]}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[min(24rem,70vh)] w-48 overflow-y-auto border-white/10 bg-ink p-1 text-foreground shadow-lg"
        role="listbox"
        aria-label={m.nav_language()}
      >
        {APP_LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            role="option"
            aria-selected={locale === current}
            onClick={() => void choose(locale)}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded-sm px-3 py-2 text-start text-sm normal-case tracking-normal transition-colors hover:bg-white/10 hover:text-ember",
              locale === current && "text-ember",
            )}
          >
            <span>{localeNativeName(locale)}</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-foreground/45">
              {LOCALE_SHORT_LABEL[locale]}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navItems = useNavItems();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => setAuthed(!!s));
    return () => subscription.unsubscribe();
  }, []);

  const rolesQ = useQuery({
    queryKey: ["my-roles"],
    queryFn: async () => {
      const { checkMyRoles } = await import("@/lib/role.functions");
      return checkMyRoles();
    },
    enabled: authed,
    retry: false,
  });

  const profileQ = useQuery({
    queryKey: ["my-profile"],
    queryFn: async () => {
      const { getMyProfile } = await import("@/lib/profile.functions");
      return getMyProfile();
    },
    enabled: authed,
    retry: false,
  });

  const displayName = profileQ.data?.full_name || profileQ.data?.email;

  const isAdmin = !!rolesQ.data?.isAdmin;
  const isEmployee = !!rolesQ.data?.isEmployee && !isAdmin;
  const rolesPending = authed && !(rolesQ.isFetched || rolesQ.isError);

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    toast.success(m.nav_toast_signed_out());
    navigate({ to: "/", replace: true });
  };

  const authLinks = authed ? (
    <>
      {rolesPending ? null : isAdmin ? (
        <HeaderLink to="/admin" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
          Admin
        </HeaderLink>
      ) : isEmployee ? (
        <HeaderLink to="/employee" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
          Schedule
        </HeaderLink>
      ) : (
        <>
          <HeaderLink
            to="/my-bookings"
            className={TOP_NAV_LINK}
            activeClassName={TOP_NAV_LINK_ACTIVE}
          >
            {m.nav_my_bookings()}
          </HeaderLink>
          <HeaderLink to="/practice" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
            {m.nav_practice_hub()}
          </HeaderLink>
        </>
      )}
      <HeaderLink to="/contact" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
        {m.nav_contact()}
      </HeaderLink>
      <button
        onClick={signOut}
        className="inline-flex shrink-0 items-center gap-1 hover:text-ember"
      >
        <LogOut className="h-3 w-3" /> {m.nav_sign_out()}
      </button>
    </>
  ) : (
    <>
      <HeaderLink to="/auth" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
        {m.nav_login()}
      </HeaderLink>
      <HeaderLink to="/contact" className={TOP_NAV_LINK} activeClassName={TOP_NAV_LINK_ACTIVE}>
        {m.nav_contact()}
      </HeaderLink>
    </>
  );

  return (
    <header className="sticky top-0 z-40 bg-ink/95 backdrop-blur supports-[backdrop-filter]:bg-ink/80">
      <div className="border-b border-white/5 bg-black">
        <div className="container-page flex h-9 items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-foreground/60">
          <div className="flex min-w-0 items-center gap-3 overflow-x-auto sm:gap-5">
            {authed && displayName && (
              <span className="shrink-0 truncate normal-case tracking-normal text-foreground/80">
                {displayName}
              </span>
            )}
            {authLinks}
          </div>
          <div className="flex shrink-0 items-center gap-3 text-foreground/65">
            <LanguageSwitcher
              authed={authed}
              profileLocale={profileQ.data?.preferred_locale ?? null}
            />
            <div className="hidden items-center gap-3 sm:flex">
              <a
                href={SITE_SOCIAL.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-ember"
              >
                <FacebookIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href={SITE_SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-ember"
              >
                <InstagramIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href={SITE_SOCIAL.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="hover:text-ember"
              >
                <YoutubeIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href={SITE_SOCIAL.twitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
                className="hover:text-ember"
              >
                <XSocialIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href={SITE_SOCIAL.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="hover:text-ember"
              >
                <LinkedinIcon className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="border-b border-white/10">
        <div className="container-page flex items-center justify-between gap-6 py-3">
          <Link to="/" className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-2 bg-ember-fill px-3 py-1.5 font-display text-lg leading-none tracking-wider text-foreground"
              style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 100%, 0 100%)" }}
            >
              {m.nav_brand()}
            </span>
          </Link>

          <nav className="hidden items-center gap-1 xl:flex">
            {navItems.map((item) => {
              if (isNavGroup(item)) {
                return <DesktopNavGroup key={item.label} item={item} pathname={pathname} />;
              }

              return (
                <HeaderLink
                  key={item.to}
                  to={item.to}
                  className={DESKTOP_NAV_LINK}
                  activeClassName={DESKTOP_NAV_LINK_ACTIVE}
                  exact={item.to === "/"}
                >
                  {item.label}
                </HeaderLink>
              );
            })}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {rolesPending ? null : isAdmin ? (
              <Link to="/admin/bookings" search={{ book: 1 }} className="btn-ember btn-ember-hover">
                Book for Member <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            ) : isEmployee ? null : (
              <Link to="/schedule" className="btn-ember btn-ember-hover">
                {m.nav_book_lesson()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="text-foreground xl:hidden"
            aria-label={m.nav_aria_toggle_menu()}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-ink xl:hidden">
          <nav className="container-page flex flex-col py-3">
            {navItems.map((item) => {
              if (isNavGroup(item)) {
                return (
                  <div key={item.label} className="flex flex-col">
                    <span className="px-3 pt-3 pb-1 font-display text-xs tracking-[0.18em] text-foreground/45 uppercase">
                      {item.label}
                    </span>
                    {item.children.map((child) => (
                      <HeaderLink
                        key={child.to}
                        to={child.to}
                        onClick={() => setOpen(false)}
                        className={
                          pathMatches(pathname, child.to)
                            ? MOBILE_NAV_SUBLINK_ACTIVE
                            : MOBILE_NAV_SUBLINK
                        }
                      >
                        {child.label}
                      </HeaderLink>
                    ))}
                  </div>
                );
              }

              return (
                <HeaderLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={MOBILE_NAV_LINK}
                  activeClassName={MOBILE_NAV_LINK_ACTIVE}
                  exact={item.to === "/"}
                >
                  {item.label}
                </HeaderLink>
              );
            })}
            {rolesPending ? null : isAdmin ? (
              <Link
                to="/admin/bookings"
                search={{ book: 1 }}
                onClick={() => setOpen(false)}
                className="btn-ember btn-ember-hover mt-3 justify-center"
              >
                Book for Member <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            ) : isEmployee ? null : (
              <Link
                to="/schedule"
                onClick={() => setOpen(false)}
                className="btn-ember btn-ember-hover mt-3 justify-center"
              >
                {m.nav_book_lesson()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            )}
            <div className="mt-4 border-t border-white/10 pt-3">
              <LanguageSwitcher
                authed={authed}
                profileLocale={profileQ.data?.preferred_locale ?? null}
                className="px-3 py-2 text-sm uppercase tracking-[0.15em] text-foreground/75"
              />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
